import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getFirestore, collection, addDoc, doc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { onIdTokenChanged } from 'firebase/auth';
import { app, auth } from '../firebase';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import RoomScheduleEditor from '../components/RoomScheduleEditor';
import { createDefaultSchedule, normalizeSchedule, DAYS_OF_WEEK } from '../utils/schedule';

const db = getFirestore(app);

export default function RoomForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [schedule, setSchedule] = useState(createDefaultSchedule());

  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [adminDomainId, setAdminDomainId] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [domains, setDomains] = useState([]);
  const [domainId, setDomainId] = useState('');

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setAuthChecked(true);
        return;
      }
      try {
        const tokenResult = await user.getIdTokenResult();
        setIsSystemAdmin(tokenResult?.claims?.role === 'SYSTEM_ADMIN');
        setAdminDomainId(tokenResult?.claims?.domainId);
      } catch (e) {
        console.error(e);
      } finally {
        setAuthChecked(true);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!isSystemAdmin) return;
    const unsub = onSnapshot(collection(db, 'domains'), (snap) => {
      setDomains(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [isSystemAdmin]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    const ref = doc(db, 'rooms', id);
    getDoc(ref).then(snap => {
      if (!snap.exists()) {
        alert('Room not found');
        navigate('/rooms');
        return;
      }
      const data = snap.data();
      setName(data.name || '');
      setDomainId(data.domainId || '');
      setSchedule(normalizeSchedule(data.schedule));
    }).catch(e => console.error('fetch room', e)).finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!authChecked) return;

    const finalDomainId = isSystemAdmin ? domainId : adminDomainId;
    if (!finalDomainId) {
      alert('Please specify a domain for the room.');
      return;
    }

    try {
      const scheduleToSave = normalizeSchedule(schedule);

      if (!scheduleToSave.alwaysOn) {
        const invalidDay = DAYS_OF_WEEK.find(({ key }) => {
          const day = scheduleToSave.days[key];
          return day.enabled && day.start >= day.end;
        });
        if (invalidDay) {
          alert(`${invalidDay.label} start time must be before the end time.`);
          return;
        }
      }

      if (isEdit) {
        await updateDoc(doc(db, 'rooms', id), { name, domainId: finalDomainId, schedule: scheduleToSave });
      } else {
        await addDoc(collection(db, 'rooms'), { name, domainId: finalDomainId, schedule: scheduleToSave, participantUids: [] });
      }
      navigate('/rooms');
    } catch (e) {
      console.error('save room', e);
      alert('Failed to save room');
    }
  };

  const handleScheduleChange = (updater) => {
    setSchedule(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return next;
    });
  };

  if (loading || !authChecked) return <Box sx={{ p: 2 }}>Loading...</Box>;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5">{isEdit ? 'Edit Room' : 'Create Room'}</Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Name" value={name} onChange={e => setName(e.target.value)} required />

        {isSystemAdmin && (
          <FormControl required>
            <InputLabel>Domain</InputLabel>
            <Select value={domainId} label="Domain" onChange={e => setDomainId(e.target.value)}>
              {domains.map(d => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <RoomScheduleEditor schedule={schedule} onChange={handleScheduleChange} />
        <Box>
          <Button type="submit" variant="contained">Save</Button>
          <Button type="button" variant="outlined" onClick={() => navigate('/rooms')} sx={{ ml: 1 }}>Cancel</Button>
        </Box>
      </Box>
    </Box>
  );
}
